import React from 'react'
import { Link } from 'react-router-dom'
import { IconArrowUpRight } from '@tabler/icons-react'
import classNames from 'classnames'

import './ExternalLink.scss'

// react-router's Link only knows how to push app routes onto history, so a link to
// another origin (a SharePoint document, say) has to be a plain anchor.
const isAbsoluteUrl = (url) => /^https?:\/\//i.test(url ?? '')

/**
 * @import import { ExternalLink } from '@/components/ExternalLink'
 * @param {object} props
 * @param {string} props.url Destination; absolute URLs render a plain anchor, app paths a router Link
 * @param {string} [props.label] Text rendered before the arrow icon
 * @param {string} [props.id] Suffix appended to the external-link test id
 * @param {boolean} [props.wrap] Allows a long label to wrap onto multiple lines
 * @param {string} [props.className] Additional classes on the link element
 * @param {boolean} [props.light] Recolors the label and icon for dark backgrounds
 */
export const ExternalLink = ({ url, label, id, wrap = false, className, light, ...props }) => {
  const sharedProps = {
    'data-testid': id ? `external-link-${id}` : 'external-link',
    className: classNames('external-link', className, { light }),
    style: { whiteSpace: wrap ? 'normal' : 'nowrap' },
  }

  const content = (
    <>
      {label && <span>{label}</span>}
      <IconArrowUpRight className="external-link-icon" />
    </>
  )

  if (isAbsoluteUrl(url)) {
    // href/target/rel come last so a caller can't drop noopener off an off-origin link
    return (
      <a {...sharedProps} {...props} href={url} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    )
  }

  const params = new URLSearchParams(url?.split('?')[1])
  const callIds = params.getAll('call_ids')
  // in testing 440 was too many but 390 was okay, so going 200 to be safe
  const maxCallIdsForLink = 200

  const routeCallExplorerLink = (url) => {
    if (callIds?.length === 1) {
      const callId = callIds[0]
      params.delete('call_ids')
      return `/calls/${callId}?${params.toString()}`
    }

    if (callIds.length > maxCallIdsForLink) {
      params.delete('call_ids')
      return `/calls?${params.toString()}`
    }

    return url
  }

  const processedUrl = url?.includes('/explorer') ? routeCallExplorerLink(url) : url

  return (
    <Link
      target="_blank"
      to={processedUrl}
      {...sharedProps}
      {...props}
      onClick={(event) => {
        // do local storage set on click, that way it will set whichever callIds you click
        // not whichever ExternalLink happened to render last
        if (callIds?.length > maxCallIdsForLink) {
          localStorage.setItem('linkedCallIds', JSON.stringify(callIds))
        }
        if (props.onClick) {
          props.onClick(event)
        }
      }}
    >
      {content}
    </Link>
  )
}
